import { useIntl } from 'react-intl';
import {
  FaEdit,
  FaFileAlt,
  FaList,
  FaUserPlus,
  FaWaveSquare,
  FaListAlt,
} from 'react-icons/fa';
import {
  Alert,
  Button,
  Col,
  Image,
  message,
  Row,
  Skeleton,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { useDisclosure } from '@/hooks';
import ContentWrapper from '@/components/_common/ContentWrapper';
import UpdateWorkOrderModal from '../../Update/UpdateWorkOrderModal';
import { useEffect, useState } from 'react';
import { useNotFoundMessage } from '@/hooks/useNotFoundMessage';
import { useHistory } from 'react-router-dom';
import CustomerWorkorderCreateModal from '@/components/Customers/CustomerWorkorderCreateModal';

import './styles.less';
import { useWorkorderDetailContext } from '../WorkorderDetailProvider';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useAccountReceivableFinancialSummary } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { InvoiceOriginReferenceEntity } from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceList } from '@/core/domain/invoice/invoices/invoices.hooks';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { useDiagnostics } from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';
import DiagnosticListModal from '@/components/_common/DiagnosticListModal';
import moment from 'moment';
import VehicleDetailsDescriptions from '@/components/_common/VehicleDetailsDescriptions';
import CustomerDetailsDescriptions from '../CustomerDetailsDescriptions';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { AuthorityClientArea } from '@/types/Authority/Authority.ClientArea';
import SentEmailValidateCodeYcarsAlert from '@/components/_common/SentEmailValidateCodeYcarsAlert';
import { useCustomerGeneral } from '@/core/domain/people/Customers/Customers.hooks';
import { UserType } from '@/core/domain/yCarsAuth/User/User.types';

function WorkOrderDetailsCard() {
  const intl = useIntl();
  const history = useHistory();
  const [hasEditCustumer, setHasEditCustumer] = useState(false);
  const [modalAddUserVisible, modalAddUserAction] = useDisclosure();
  const [modalHistoryDiagnosticsOpen, modalHistoryDiagnosticsActions] =
    useDisclosure();

  const {
    workorder: { data: workOrder, isLoading, refetch: workOrderRefetch },
    complement: { data: complement, refetch: complementRefetch },
  } = useWorkorderDetailContext();

  const hasComplementClosed = workOrder?.complements.some(
    (complement) => complement.status === WorkOrderComplementStatus.Closed,
  );

  const { data: financial } = useAccountReceivableFinancialSummary(
    workOrder?.id,
  );

  const { data: invoicesData } = useInvoiceList({
    entity: InvoiceOriginReferenceEntity.WORK_ORDER,
    entityId: workOrder?.complements.map((item) => item.id),
    companyId: workOrder?.company.id,
  });

  const customerDetail = useCustomerGeneral(workOrder?.customer?.id);

  const invoices = flatInfiniteData(invoicesData);

  const [updateVisible, updateActions] = useDisclosure(false, {
    onClose() {
      if (workOrderRefetch) {
        workOrderRefetch();
      }
      if (complementRefetch) {
        complementRefetch();
      }
    },
  });

  const notFountMessage = useNotFoundMessage('workOrders.domain.customer');

  useEffect(() => {
    if (workOrder && workOrder.customer === null) {
      message.info({ content: notFountMessage, onClick: updateActions.open });
    }
  }, [workOrder]);

  useEffect(() => {
    if (financial) {
      setHasEditCustumer(
        (financial.totalUnprocessedInsurerValue || 0) ===
          financial.totalInsurerValue &&
          (financial.totalUnprocessedPrivateValue || 0) ===
            financial.totalPrivateValue,
      );
    }
  }, [financial]);

  const customer = workOrder?.customer;
  const vehicleData = workOrder?.vehicle;

  const { data } = useDiagnostics({
    licensePlate: workOrder?.vehicle?.licensePlate,
  });

  const diagnostics = flatInfiniteData(data);

  const hasCustomerAreaPermission =
    hasPermissions([AuthorityClientArea.CLIENT_AREA]) &&
    !!customerDetail.data?.cpfCnpj;

  return (
    <>
      <ContentWrapper style={{ paddingBottom: '30px' }}>
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }} align="middle">
          <Col flex={1}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {intl.formatMessage({ id: 'workOrders.details.clientsData' })}
            </Typography.Title>
          </Col>

          <Col>
            <Space
              size="large"
              direction="horizontal"
              style={{ flexFlow: 'wrap' }}
            >
              <Tooltip title="Edição rápida de cliente">
                <Button
                  type="primary"
                  ghost
                  icon={<AntdFeatherIcon icon={<FaEdit />} size={16} />}
                  onClick={updateActions.toggle}
                  disabled={hasComplementClosed}
                  shape="round"
                />
              </Tooltip>

              <Tooltip title="Cadastro rápido de cliente">
                <Button
                  type="primary"
                  ghost
                  shape="round"
                  icon={<AntdFeatherIcon icon={<FaUserPlus />} size={16} />}
                  onClick={modalAddUserAction.open}
                  disabled={
                    hasComplementClosed || !hasEditCustumer || !!invoices.length
                  }
                />
              </Tooltip>

              <Tooltip title="Dados do cliente">
                <Button
                  type="primary"
                  ghost
                  shape="round"
                  icon={<AntdFeatherIcon icon={<FaFileAlt />} size={16} />}
                  onClick={() =>
                    history.push(
                      `/app/customers/${workOrder?.customer?.id}/general`,
                    )
                  }
                />
              </Tooltip>

              <Tooltip title="Histórico do veículo">
                <Button
                  type="primary"
                  ghost
                  shape="round"
                  icon={<AntdFeatherIcon icon={<FaList />} size={16} />}
                  disabled={!vehicleData}
                  onClick={() =>
                    history.push(
                      `/app/history/vehiclesHistory/${vehicleData?.licensePlate}/detail`,
                    )
                  }
                />
              </Tooltip>

              <Tooltip title="Diagnóstico">
                <Button
                  type="primary"
                  shape="round"
                  icon={<AntdFeatherIcon icon={<FaWaveSquare />} size={16} />}
                  disabled={!workOrder?.diagnosticId}
                  onClick={() =>
                    window.open(
                      `/app/movements/diagnostics/${workOrder?.diagnosticId}`,
                      '_blank',
                    )
                  }
                />
              </Tooltip>

              <Tooltip title="Histórico de Diagnósticos">
                <Button
                  type="primary"
                  shape="round"
                  icon={<AntdFeatherIcon icon={<FaListAlt />} size={16} />}
                  disabled={!diagnostics.length || !vehicleData?.licensePlate}
                  onClick={modalHistoryDiagnosticsActions.open}
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>

        <Skeleton active loading={isLoading}>
          <Skeleton active loading={customerDetail.isLoading}>
            {hasCustomerAreaPermission && (
              <SentEmailValidateCodeYcarsAlert
                ycarsStatus={customerDetail.data?.ycarsStatus}
                user={{
                  cpfCnpj: customerDetail.data?.cpfCnpj,
                  id: customerDetail.data?.id as number,
                  name: customerDetail.data?.name,
                  type: UserType.customer,
                  rentalCompany: customerDetail.data?.rentalCompany as boolean,
                }}
              />
            )}
          </Skeleton>

          <CustomerDetailsDescriptions />

          <VehicleDetailsDescriptions />

          {customer?.birthDate &&
            moment(customer.birthDate).format('DD/MM') ===
              moment().format('DD/MM') && (
              <Alert
                style={{ marginTop: 24 }}
                type="success"
                icon={<span style={{ fontSize: 32 }}>🥳</span>}
                showIcon
                description={
                  <>
                    <strong>Hoje é o aniversário do {customer.name}!!!</strong>
                    <div>
                      Aproveite este momento para tornar o dia do seu cliente
                      ainda mais especial!
                    </div>
                  </>
                }
              />
            )}

          {!!complement?.authorizedSignature && (
            <Space
              style={{ width: '100%', textAlign: 'center', display: 'flex' }}
              direction="vertical"
            >
              <Image
                src={complement?.authorizedSignature}
                style={{
                  maxWidth: '350px',
                  marginTop: 30,
                  padding: 8,
                  backgroundColor: 'rgb(245, 245, 245)',
                }}
                preview={false}
              />

              <div style={{ marginTop: 0 }}>Assinatura digital do cliente</div>
            </Space>
          )}
        </Skeleton>
      </ContentWrapper>

      <UpdateWorkOrderModal
        open={updateVisible}
        onCancel={updateActions.close}
      />

      <CustomerWorkorderCreateModal
        visible={modalAddUserVisible}
        onCancel={modalAddUserAction.close}
        onConfirm={workOrderRefetch}
      />

      <DiagnosticListModal
        title={`Histórico de diagnósticos do veículo de placa: ${vehicleData?.licensePlate}`}
        open={modalHistoryDiagnosticsOpen}
        onCancel={modalHistoryDiagnosticsActions.close}
        enabled={!!vehicleData?.licensePlate}
        queryFilter={{ licensePlate: vehicleData?.licensePlate }}
      />
    </>
  );
}

export default WorkOrderDetailsCard;
